import React, { useEffect, useRef, useState } from 'react';

import { type AnimateNumberReturn } from '@pkg/hooks';
import { useAnimateNumber } from '@pkg/hooks/useAnimateNumber/useAnimateNumber';

import { tokens } from './tokens';

import { Action, ConicTimer, Content, ExternalLink, Interior, InternalLink, Label } from './styles';

export interface TimedActionProps {
    label: string;
    duration?: number;
    compact?: boolean;
    paused?: boolean;
    disabled?: boolean;
    external?: boolean;
    ariaLabel?: string;
    url?: string;
    onAction?(): void;
    onExpire?(): void;
    onInteractBegin?(): void;
    onInteractEnd?(): void;
}

const MIN_DEGREE = 0;
const MAX_DEGREE = 360;

export function TimedAction({
    label,
    duration = tokens.conicTimer.duration,
    compact = false,
    paused = false,
    disabled = false,
    external = false,
    ariaLabel,
    url,
    onAction,
    onExpire,
    onInteractBegin,
    onInteractEnd,
}: TimedActionProps) {
    const [degree, setDegree] = useState(MIN_DEGREE);
    const animationRef = useRef<AnimateNumberReturn>();

    // This could be a `const` instead, we would just
    // need to ignore it as a `useEffect` dependency.
    animationRef.current = useAnimateNumber(MIN_DEGREE, MAX_DEGREE, {
        duration,
        roundValue: true,
        onUpdate: (value) => setDegree(value),
        onComplete: onExpire,
    });

    useEffect(() => {
        const progress = animationRef.current?.getProgress() ?? 0;
        const completed = progress >= 100;

        if (Boolean(disabled || paused) && !completed) {
            animationRef.current?.stop();
        } else if (!completed) {
            animationRef.current?.start();
        }
    }, [disabled, paused]);

    const childrenMarkup = (
        <Interior>
            <ConicTimer $degree={degree} />
            <Content>
                <Label>{label}</Label>
            </Content>
        </Interior>
    );

    const commonProps = {
        $compact: compact,
        onMouseEnter: onInteractBegin,
        onFocus: onInteractBegin,
        onMouseLeave: onInteractEnd,
        onBlur: onInteractEnd,
    };

    if (url?.length) {
        return external ? (
            <ExternalLink {...commonProps} $disabled={disabled} href={url}>
                {childrenMarkup}
            </ExternalLink>
        ) : (
            <InternalLink {...commonProps} $disabled={disabled} to={url}>
                {childrenMarkup}
            </InternalLink>
        );
    }

    // `disabled` is repeated twice style props + actual attribute.
    const actionIsDisabled = Boolean(disabled || !onAction);

    return (
        <Action
            {...commonProps}
            $disabled={actionIsDisabled}
            disabled={actionIsDisabled}
            aria-label={ariaLabel}
            onClick={onAction}
        >
            {childrenMarkup}
        </Action>
    );
}
